import graphql from 'babel-plugin-relay/macro';
import {FormikHelpers} from 'formik';
import React from 'react';
import {useLazyLoadQuery, useMutation} from 'react-relay/hooks';
import {useHistory, useParams} from 'react-router';
import {showEventNotification} from 'src/utils/notifications';
import {withSuspense} from 'src/utils/withSuspense';

import Form from '../components/Form';
import {FormValues} from '../types';
import {updatePageUpdateValveManufacturerMutation} from './__generated__/updatePageUpdateValveManufacturerMutation.graphql';
import {updatePageValveManufacturerQuery} from './__generated__/updatePageValveManufacturerQuery.graphql';

function UpdatePage() {
  const {id} = useParams<{id: string}>();
  const history = useHistory();

  const [commit, isInFlight] = useMutation<updatePageUpdateValveManufacturerMutation>(
    updateValveManufacturerMutation,
  );

  const data = useLazyLoadQuery<updatePageValveManufacturerQuery>(query, {
    valveManufacturerId: id,
  });

  if (!data.valveManufacturer) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <h4 className="text-lg text-gray-700">The item you wish to update could not be found.</h4>
      </div>
    );
  }

  const initialValues = handleInitialValues();

  return (
    <Form
      title="Update Valve Manufacturer"
      button="Update Valve Manufacturer"
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isSubmitting={isInFlight}
    />
  );

  function handleInitialValues() {
    if (!data || !data.valveManufacturer) {
      return;
    }

    const {valveManufacturer} = data;

    return {
      name: valveManufacturer.name,
    };
  }

  function handleSubmit(values: FormValues, formikHelpers: FormikHelpers<FormValues>) {
    commit({
      variables: {
        input: {
          valveManufacturerId: id,
          updateValveManufacturerData: values,
        },
      },
      onCompleted: payload => {
        if (
          payload.updateValveManufacturer?.errors[0] &&
          payload.updateValveManufacturer?.errors[0].fields
        ) {
          payload.updateValveManufacturer?.errors[0].fields.forEach(fieldError => {
            formikHelpers.setFieldError(fieldError.fieldName, fieldError.message);
          });
        }

        const entry = payload.updateValveManufacturer?.valveManufacturer;

        if (entry) {
          showEventNotification('updated', 'Manufacturer', entry.name);

          if (history.location.search.includes('newTab')) {
            window.close();
          }

          return history.push('/admin/valve_manufacturers');
        }
      },
    });
  }
}

const query = graphql`
  query updatePageValveManufacturerQuery($valveManufacturerId: ID!) {
    valveManufacturer(valveManufacturerId: $valveManufacturerId) {
      id
      createdAt
      name
    }
  }
`;

const updateValveManufacturerMutation = graphql`
  mutation updatePageUpdateValveManufacturerMutation($input: UpdateValveManufacturerInput!) {
    updateValveManufacturer(input: $input) {
      valveManufacturer {
        id
        createdAt
        name
      }
      errors {
        code
        fields {
          fieldName
          message
        }
      }
    }
  }
`;

export default withSuspense(UpdatePage);
